import {
  FormattedHTMLMessage,
  FormattedMessage,
  defineMessages,
  injectIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';

import { accountLogout } from '../../../../ducks/user/auth';
import { getImageUrl, navigate } from '../../../../helpers';
import { getTicketTypes, getTickets, postClaimFreeTicket } from '../../../../ducks/event';
import { isMobile } from '../../../../mobile/helpers';
import Flash from '../../components/Flash';
import Option from '../components/Option';
import styles from '../seatmap.scss';
import withUser from '../../../../components/withUser';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  dispatch: PropTypes.func.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    logo_id: PropTypes.string,
    shortcode: PropTypes.string,
    purchase_url: PropTypes.string,
    seatmap_published: PropTypes.bool,
  }),
  loading: PropTypes.bool,
  tickets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  ticketTypes: PropTypes.shape({
    filter: PropTypes.func,
  }),
};

const messages = defineMessages({
  chooseASeat: {
    id: 'seatmap.manage.chooseASeat',
    defaultMessage: 'Choose a seat',
  },
  seatmapPublished: {
    id: 'seatmap.manage.seatmapPublished',
    defaultMessage: '{seatmapPublished, select, true {Choose or change seats for your tickets.} other {The seatmap has not been released yet. You will get an email when it is released.}}',
  },
  giveTitle: {
    id: 'seatmap.manage.giveTitle',
    defaultMessage: 'Give a ticket away',
  },
  giveDescription: {
    id: 'seatmap.manage.giveDescription',
    defaultMessage: 'Give a ticket away to a friend or family member.',
  },
  viewTitle: {
    id: 'seatmap.manage.viewTitle',
    defaultMessage: 'View tickets',
  },
  viewDescription: {
    id: 'seatmap.manage.viewDescription',
    defaultMessage: 'View your tickets for check-in.',
  },
  signRegTitle: {
    id: 'seatmap.manage.signRegTitle',
    defaultMessage: 'Sign in or register',
  },
  signRegDescription: {
    id: 'seatmap.manage.signRegDescription',
    defaultMessage: 'Sign in or register to view and manage your tickets.',
  },
  seatmapTitle: {
    id: 'seatmap.manage.seatmapTitle',
    defaultMessage: 'Look at the seatmap',
  },
  seatmapDescription: {
    id: 'seatmap.manage.seatmapDescription',
    defaultMessage: '{seatmapPublished, select, true {Just take a look at how the seatmap looks.} other {The seatmap has not been released yet. Come back closer to the event to see the seatmap.}}',
  },
  signOutTitle: {
    id: 'seatmap.manage.signOutTitle',
    defaultMessage: 'Sign out',
  },
  signOutDescription: {
    id: 'seatmap.manage.signOutDescription',
    defaultMessage: 'You are logged in as {userName}',
  },
  buyTicketsTitle: {
    id: 'seatmap.manage.buyTicketsTitle',
    defaultMessage: 'Buy tickets',
  },
  buyTicketsDescription: {
    id: 'seatmap.manage.buyTicketsDescription',
    defaultMessage: 'Jump to the ticket shop to purchase',
  },
  claimTicketsTitle: {
    id: 'seatmap.manage.claimTicketsTitle',
    defaultMessage: '{claimed, select, true {Claim free ticket (claimed)} other {Claim free ticket}}',
  },
  claimTicketsTitleNotLoggedIn: {
    id: 'seatmap.manage.claimTicketsTitleNotLoggedIn',
    defaultMessage: 'Claim free ticket (login first)',
  },
  notLoggedIn: {
    id: 'seatmap.manage.notLoggedIn',
    defaultMessage: 'You have to login first!',
  },
});

class Manage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    const {
      dispatch,
      user,
      event,
      tickets,
    } = this.props;

    if (event) {
      dispatch(getTicketTypes(event.id));
    }
    if (user && user.id && event && !(tickets?.length > 0)) {
      dispatch(getTickets(event.id));
    }
  }

  componentDidUpdate(prevProps) {
    const { event: oldEvent, user: oldUser } = prevProps;
    const { event, user, dispatch } = this.props;

    if ((user && !oldUser) || (!oldEvent && event) || (oldEvent && oldEvent.id !== event.id)) {
      dispatch(getTickets(event.id));
      dispatch(getTicketTypes(event.id));
    }
  }

  render() {
    const {
      history,
      match,
      tickets,
      user,
      event,
      loading,
      dispatch,
      intl,
      ticketTypes,
    } = this.props;
    const {
      claimLoading,
    } = this.state;
    const { formatMessage } = intl;

    const claimableTickets = ticketTypes && ticketTypes.filter(
      (item) => item.freeToClaim && !item.requireInvite,
    );

    const onClaimFreeTicket = (id) => () => {
      if (!user) {
        this.setState({ claimError: '' }, () => this.setState({ claimError: formatMessage(messages.notLoggedIn) }));
        return;
      }

      this.setState({ claimLoading: true });
      dispatch(postClaimFreeTicket(event.id, id))
        .finally(() => this.setState({ claimLoading: false }));
    };

    return (
      <>
        <Flash
          message={this.state.claimError}
          error
        />

        { !loading
          && (
            <>
              <div className={styles.title}>
                {event.logo_id && <img src={getImageUrl(event.logo_id)} style={{ width: '100%' }} alt="logo" />}
                <FormattedMessage
                  id="seatmap.manage.title"
                  defaultMessage="What would you like to do?"
                />
              </div>
              { event && tickets && tickets?.length > 0
              && (
              <div className={styles.description}>
                <FormattedHTMLMessage
                  id="seatmap.manage.description"
                  defaultMessage="You have <strong>{ticketCount, number}</strong> {ticketCount, plural, one {ticket} other {tickets}} for <strong>{eventName}</strong>. You can view them and place them on seats, or give tickets away to friends or family."
                  values={{
                    ticketCount: tickets?.length || 0,
                    eventName: event.name,
                  }}
                />
              </div>
              )}
              {claimableTickets && claimableTickets.map((item) => {
                const alreadyClaimed = tickets.find(
                  (ticket) => ticket.ticket_type_id === item.id,
                ) !== undefined;

                return (
                  <Option
                    key={item.id}
                    icon="hand-holding-heart"
                    title={formatMessage(
                      user
                        ? messages.claimTicketsTitle
                        : messages.claimTicketsTitleNotLoggedIn,
                      { claimed: alreadyClaimed },
                    )}
                    description={item.name}
                    disabled={alreadyClaimed || !user}
                    loading={claimLoading}
                    onClick={!alreadyClaimed ? onClaimFreeTicket(item.id) : undefined}
                  />
                );
              })}
              {claimableTickets && claimableTickets?.length > 0 && (
                <div className={styles.divider} />
              )}
              { user && user.id
                ? (
                  <>
                    <Option
                      icon="map-marked"
                      title={formatMessage(messages.chooseASeat)}
                      description={formatMessage(
                        messages.seatmapPublished,
                        { seatmapPublished: event.seatmap_published },
                      )}
                      disabled={!event.seatmap_published}
                      onClick={event.seatmap_published
                        ? () => navigate(history, `/tickets/${event.shortcode}/list/place`)
                        : null}
                    />
                    <Option
                      icon="gift"
                      title={formatMessage(messages.giveTitle)}
                      description={formatMessage(messages.giveDescription)}
                      onClick={() => navigate(history, `/tickets/${event.shortcode}/list/gift`)}
                    />
                    <Option
                      icon="ticket-alt"
                      title={formatMessage(messages.viewTitle)}
                      description={formatMessage(messages.viewDescription)}
                      onClick={() => navigate(history, `/app/event/${event.shortcode}/ticket`)}
                    />
                  </>
                )
                : (
                  <Option
                    icon="sign-in"
                    title={formatMessage(messages.signRegTitle)}
                    description={formatMessage(messages.signRegDescription)}
                    onClick={() => navigate(history, `/login/${btoa(match.url)}`)}
                  />
                )}
              <Option
                icon="map"
                title={formatMessage(messages.seatmapTitle)}
                description={formatMessage(
                  messages.seatmapDescription,
                  { seatmapPublished: event.seatmap_published },
                )}
                disabled={!event.seatmap_published}
                onClick={event.seatmap_published
                  ? () => navigate(history, `/tickets/${event.shortcode}/seatmap`)
                  : null}
              />

              {event.purchase_url
              && (
              <Option
                key={event.id}
                icon="shopping-basket"
                title={formatMessage(messages.buyTicketsTitle)}
                description={formatMessage(messages.buyTicketsDescription)}
                href={event.purchase_url}
              />
              )}

              { user && user.id
              && (
                <>
                  <div className={styles.divider} />
                  <Option
                    icon="sign-out"
                    title={formatMessage(messages.signOutTitle)}
                    description={formatMessage(
                      messages.signOutDescription,
                      { userName: user.name },
                    )}
                    onClick={isMobile() && window
                      ? window.logout : () => dispatch(accountLogout())}
                  />
                </>
              )}
            </>
          )}
      </>
    );
  }
}

Manage.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    event: state.getIn(['event', 'data']),
    ticketTypes: state.getIn(['event', 'ticket_types']),
    error: state.getIn(['event', 'error']),
    loading: state.getIn(['event', 'loading']),
    tickets: state.getIn(['event', 'tickets']),
  };
}

export default injectIntl(withUser(connect(mapStateToProps)(Manage)));
